import { useCallback } from 'react';
import { ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';

import CheckCircleFilledIcon from 'modules/common/components/Icons/CheckCircleFilledIcon';
import { StepProgressRing } from 'modules/common/components/MUI/FullScreenDialog/VerticalStepper';

export interface SetupNavigatorStep {
    label: string;
    /**
     * The wizard routes this step covers, in order. A click lands on the last one, which is where the
     * footer's Back from the next step lands.
     */
    paths: readonly string[];
    /** Whether the step shows as done. The caller decides what counts, the navigator never infers it. */
    isComplete?: boolean;
    /**
     * Set when the footer's Back on the next step's first screen lands on this step. Off by default.
     * The navigator then only shortcuts Back presses the footer already allows, never a way around a
     * step's own rules: a click reaches a step only if every step between it and the current one has
     * this set, so a blocked step cannot be jumped over.
     */
    canRevisit?: boolean;
}

type StepState = 'current' | 'complete' | 'upcoming';

interface SetupStepNavigatorProps {
    steps: readonly SetupNavigatorStep[];
    activePath: string;
    /** Rows are clickable only when this is passed. */
    onStepClick?: (path: string) => void;
    /** Turns every row back into plain progress, e.g. while the wizard is saving a step. */
    disabled?: boolean;
}

const StepList = styled('ol')(({ theme }) => ({
    listStyle: 'none',
    margin: 0,
    padding: theme.spacing(3, 0),
    minWidth: theme.spacing(27),
}));

const rowStyles = (theme: Theme, state: StepState) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1.5),
    width: '100%',
    height: theme.spacing(5),
    padding: theme.spacing(0, 1.5),
    borderRadius: theme.spacing(1),
    textAlign: 'left' as const,
    backgroundColor: state === 'current' ? theme.palette.purple[50] : 'transparent',
});

const shouldForwardProp = (prop: PropertyKey) => prop !== 'state';

// A row the member cannot click is plain content, not a disabled button: screen readers would announce
// a disabled control as unavailable, and it would drop out of the tab order along with aria-current.
const StaticStepRow = styled('div', { shouldForwardProp })<{ state: StepState }>(({ theme, state }) =>
    rowStyles(theme, state)
);

// Only a step behind the current one is ever a button, so the hover never touches the current step's
// highlight.
const ButtonStepRow = styled(ButtonBase, { shouldForwardProp })<{ state: StepState }>(({ theme, state }) => ({
    ...rowStyles(theme, state),
    '&:hover': {
        backgroundColor: theme.palette.cement[50],
    },
    '&.Mui-focusVisible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
    },
}));

const UpcomingIcon = styled('span')(({ theme }) => ({
    flexShrink: 0,
    width: theme.spacing(2),
    height: theme.spacing(2),
    borderRadius: '50%',
    border: `1px solid ${theme.palette.grey[200]}`,
}));

// Each hop from the current step back to the target has to be one the footer allows.
function canGoBackTo(steps: readonly SetupNavigatorStep[], index: number, activeIndex: number): boolean {
    return index < activeIndex && steps.slice(index, activeIndex).every((step) => step.canRevisit);
}

function getStepState(index: number, activeIndex: number, isComplete: boolean): StepState {
    if (index === activeIndex) {
        return 'current';
    }
    return isComplete ? 'complete' : 'upcoming';
}

// How far through the current step the member is: one share per route, plus a final share the check
// mark takes, so the ring never reads as full before the step is actually done. Two routes on the
// first one is a third of the ring, on the second two thirds.
function getStepProgress(step: SetupNavigatorStep, activePath: string): number {
    return (step.paths.indexOf(activePath) + 1) / (step.paths.length + 1);
}

interface StepIconProps {
    step: SetupNavigatorStep;
    state: StepState;
    activePath: string;
}

function StepIcon({ step, state, activePath }: Readonly<StepIconProps>) {
    const theme = useTheme();

    if (state === 'complete') {
        return (
            <CheckCircleFilledIcon
                data-testid="SetupStepCompleteIcon"
                fill={theme.palette.success.main}
                sx={{ width: 16, height: 16 }}
            />
        );
    }
    if (state === 'current') {
        return <StepProgressRing progress={getStepProgress(step, activePath)} size={16} />;
    }
    return <UpcomingIcon />;
}

function StepContent({ step, state, activePath }: Readonly<StepIconProps>) {
    return (
        <>
            <StepIcon step={step} state={state} activePath={activePath} />
            <Typography variant="subtitle2" color={state === 'current' ? 'text.primary' : 'text.secondary'}>
                {step.label}
            </Typography>
        </>
    );
}

interface StepItemProps {
    step: SetupNavigatorStep;
    state: StepState;
    activePath: string;
    /** Present only when the member can go back to this step right now. */
    onStepClick?: (path: string) => void;
}

function StepItem({ step, state, activePath, onStepClick }: Readonly<StepItemProps>) {
    const handleClick = useCallback(() => onStepClick?.(step.paths[step.paths.length - 1]), [onStepClick, step.paths]);
    const ariaCurrent = state === 'current' ? 'step' : undefined;

    return (
        <li>
            {onStepClick ? (
                <ButtonStepRow state={state} onClick={handleClick}>
                    <StepContent step={step} state={state} activePath={activePath} />
                </ButtonStepRow>
            ) : (
                <StaticStepRow state={state} aria-current={ariaCurrent}>
                    <StepContent step={step} state={state} activePath={activePath} />
                </StaticStepRow>
            )}
        </li>
    );
}

/**
 * Left-hand navigator for a multi-step setup wizard. A step behind the current one is a button back to
 * it when the footer could walk the member there (see `canRevisit`); every other step only shows
 * progress. Later steps are never clickable, since moving forward has to go through the step's own
 * Continue and whatever it saves on exit.
 */
export default function SetupStepNavigator({
    steps,
    activePath,
    onStepClick,
    disabled = false,
}: Readonly<SetupStepNavigatorProps>) {
    const activeIndex = steps.findIndex((step) => step.paths.includes(activePath));

    return (
        <nav aria-label="Setup steps">
            <Stack component={StepList} spacing={2}>
                {steps.map((step, index) => (
                    <StepItem
                        key={step.label}
                        step={step}
                        state={getStepState(index, activeIndex, Boolean(step.isComplete))}
                        activePath={activePath}
                        onStepClick={!disabled && canGoBackTo(steps, index, activeIndex) ? onStepClick : undefined}
                    />
                ))}
            </Stack>
        </nav>
    );
}
